import { useEffect, useState } from 'react';

import type { ApolloCache } from '@apollo/client';
import type { ApolloLink } from '@apollo/client/link';
import { useMutation, useQuery } from '@apollo/client/react';
import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Field } from '@opendoor/atoms/field';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { Navigate } from 'react-router';

import { Loader } from '../../components/Loader';
import { apolloClient } from '../../config/apolloClients';
import { useToast } from '../../contexts/ToastContext';
import { CreateOrUpdateWebhookUrlDocument } from '../../graphql/mutations/__generated__/CreateOrUpdateWebhookUrl';
import type { CreateOrUpdateWebhookUrl } from '../../graphql/mutations/__generated__/CreateOrUpdateWebhookUrl';
import { DeleteWebhookUrlDocument } from '../../graphql/mutations/__generated__/DeleteWebhookUrl';
import type { DeleteWebhookUrl } from '../../graphql/mutations/__generated__/DeleteWebhookUrl';
import { GetWebhookUrlDocument } from '../../graphql/queries/__generated__/GetWebhookUrl';
import { getErrorMessage } from '../../helpers/formatUtils';
import { ERROR_PAGE_ROUTE } from '../routes';

const Webhooks = () => {
  const [webhookValue, setWebhookValue] = useState('');
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);

  const { showToast } = useToast();

  /*
   * Callback for the create/update mutation hook to update the cached query response with the saved webhook data
   */
  const handleWebhookUpdate = (
    cache: ApolloCache,
    { data }: ApolloLink.Result<CreateOrUpdateWebhookUrl>,
  ) => {
    if (data?.createOrUpdateWebhookUrl.success) {
      cache.writeQuery({
        query: GetWebhookUrlDocument,
        data: {
          webhookUrl: data.createOrUpdateWebhookUrl.webhookUrl,
        },
      });
      showToast({ type: 'success', message: 'Your webhook endpoint has been saved.' });
    }
  };

  const handleWebhookDelete = (
    cache: ApolloCache,
    { data }: ApolloLink.Result<DeleteWebhookUrl>,
  ) => {
    if (data?.deleteWebhookUrl) {
      cache.writeQuery({
        query: GetWebhookUrlDocument,
        data: {
          webhookUrl: null,
        },
      });
      setWebhookValue('');
      showToast({ type: 'success', message: 'Your webhook endpoint has been deleted.' });
    }
  };

  const { loading, error, data } = useQuery(GetWebhookUrlDocument, {
    client: apolloClient,
  });

  const [addWebhook, { loading: addWebhookLoading, error: addWebhookError }] = useMutation(
    CreateOrUpdateWebhookUrlDocument,
    {
      client: apolloClient,
      update: handleWebhookUpdate,
    },
  );

  const [deleteWebhook, { loading: deleteWebhookLoading, error: deleteWebhookError }] = useMutation(
    DeleteWebhookUrlDocument,
    {
      client: apolloClient,
      update: handleWebhookDelete,
    },
  );

  // Initialize webhook value from query data on first load only
  // State-based flag ensures proper reset on component remount
  useEffect(() => {
    if (!loading && data?.webhookUrl && !hasLoadedInitialData) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      setWebhookValue(data.webhookUrl);
      setHasLoadedInitialData(true);
    }
  }, [loading, data?.webhookUrl, hasLoadedInitialData]);

  // If there is an error from the get, then we have to hard fail
  if (error) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }

  const handleSave = () => {
    addWebhook({ variables: { webhookUrl: webhookValue } });
  };

  const handleDelete = () => {
    deleteWebhook();
  };

  const mutationError = addWebhookError ?? deleteWebhookError;

  return (
    <Col className="max-w-full gap-8 rounded-2xl bg-od-bg-secondary p-12">
      <Text as="h2" className="text-header-md font-medium">
        Webhooks
      </Text>
      <Text as="p" className="text-body-md">
        Use webhooks to get notified about events that are relevant to your application. For now, we
        only support state transition events for offers initiated from your application.
      </Text>
      <Col>
        <a
          target="_blank"
          href="https://docs.opendoor.com/api-reference/receive-status-updates"
          rel="noopener noreferrer"
        >
          Learn more →
        </a>
      </Col>
      <Col className="pt-6">
        {loading ? (
          <Col>
            <Loader size="large" />
          </Col>
        ) : (
          <Row className="items-end gap-8">
            <Col className="w-100">
              <Field
                label="Endpoint"
                id="webhook-input"
                error={mutationError ? getErrorMessage(mutationError) : null}
              >
                <Input
                  id="webhook-input"
                  className="w-full"
                  value={webhookValue}
                  onChange={(event) => setWebhookValue(event.currentTarget.value)}
                  placeholder="e.g. https://your-app.com/api/webhook"
                  analyticsName="partnership-dashboard-fe-webhook-input"
                  error={Boolean(mutationError)}
                />
              </Field>
            </Col>
            <Button
              onClick={handleSave}
              loading={addWebhookLoading}
              variant="primary"
              analyticsName="partnership-dashboard-fe-save-webhook"
              aria-label="Save Webhook"
            >
              Save
            </Button>
            {data && data.webhookUrl && (
              <Button
                onClick={handleDelete}
                loading={deleteWebhookLoading}
                variant="secondary"
                analyticsName="partnership-dashboard-fe-delete-webhook"
                aria-label="Delete Webhook"
              >
                Delete
              </Button>
            )}
          </Row>
        )}
      </Col>
    </Col>
  );
};

export default Webhooks;
